/**
 * PlayerResultCard - Visual player card for Knewton chat results
 * Shows player info, streak/prediction data, and Add to Parlay button
 */

import {
  PlusCircleIcon,
  CheckCircleIcon,
  FireIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/solid';

/**
 * Get color based on hit rate percentage
 */
const getHitRateColor = (hitRate) => {
  if (hitRate >= 90) return 'text-green-400';
  if (hitRate >= 80) return 'text-emerald-400';
  if (hitRate >= 70) return 'text-yellow-400';
  return 'text-orange-400';
};

/**
 * Get background gradient based on type (streak vs prediction)
 */
const getCardGradient = (type, hitRate) => {
  if (type === 'streak') {
    if (hitRate >= 90) return 'from-green-900/40 to-emerald-900/20';
    if (hitRate >= 80) return 'from-emerald-900/40 to-teal-900/20';
    return 'from-yellow-900/40 to-amber-900/20';
  }
  // Prediction type
  return 'from-blue-900/40 to-indigo-900/20';
};

/**
 * Get badge for streak/prediction type
 */
const getBadge = (type, hitRate) => {
  if (type === 'streak') {
    if (hitRate >= 90) return { icon: FireIcon, text: 'BLAZING', color: 'bg-orange-500/20 text-orange-400 border-orange-500/40' };
    if (hitRate >= 80) return { icon: FireIcon, text: 'HOT', color: 'bg-red-500/20 text-red-400 border-red-500/40' };
    return { icon: ArrowTrendingUpIcon, text: 'STREAK', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40' };
  }
  return { icon: ChartBarIcon, text: 'PREDICTED', color: 'bg-blue-500/20 text-blue-400 border-blue-500/40' };
};

export default function PlayerResultCard({ player, onAdd, onRemove, isInParlay }) {
  const {
    player_name,
    team_abbr,
    position,
    headshot_url,
    stat_type,
    stat_label,
    threshold,
    hit_rate,
    games_window,
    predicted_value,
    confidence,
    opponent_abbr,
    opponent_strength,
    type = 'streak', // 'streak' or 'prediction'
    reasoning
  } = player;

  const hitRatePct = hit_rate ? Math.round(hit_rate * 100) : null;
  const badge = getBadge(type, hitRatePct);
  const BadgeIcon = badge.icon;

  // Visual bar for hit rate or confidence
  const progressValue = type === 'streak' ? hitRatePct : confidence;
  const progressLabel = type === 'streak' ? `${hitRatePct}%` : `${Math.round(confidence)}%`;

  return (
    <div className={`
      relative overflow-hidden rounded-xl border border-gray-700/50
      bg-gradient-to-br ${getCardGradient(type, hitRatePct)}
      hover:border-purple-500/40 transition-all duration-200
      group
    `}>
      {/* Main content */}
      <div className="p-3">
        {/* Header row - Player info and badge */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2.5">
            {/* Player avatar */}
            <div className="relative">
              {headshot_url ? (
                <img
                  src={headshot_url}
                  alt={player_name}
                  className="w-11 h-11 rounded-full object-cover bg-gray-700 border-2 border-gray-600"
                />
              ) : (
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center border-2 border-gray-600">
                  <span className="text-lg font-bold text-gray-300">
                    {player_name?.charAt(0) || '?'}
                  </span>
                </div>
              )}
              {/* Team badge */}
              <div className="absolute -bottom-1 -right-1 bg-gray-800 px-1.5 py-0.5 rounded text-[9px] font-bold text-gray-300 border border-gray-600">
                {team_abbr}
              </div>
            </div>

            {/* Player name and position */}
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-white text-sm truncate">{player_name}</h4>
              <p className="text-xs text-gray-400">{position}</p>
            </div>
          </div>

          {/* Type badge */}
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full border ${badge.color} text-[10px] font-bold`}>
            <BadgeIcon className="w-3 h-3" />
            {badge.text}
          </div>
        </div>

        {/* Stat display - Large and prominent */}
        <div className="bg-gray-900/50 rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">
                {type === 'streak' ? 'Threshold' : 'Prediction'}
              </p>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-black text-white">
                  {type === 'streak' ? `${threshold}+` : predicted_value?.toFixed(1)}
                </span>
                <span className="text-sm text-gray-400">{stat_label || stat_type}</span>
              </div>
            </div>

            {/* Hit rate / Confidence meter */}
            <div className="text-right">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">
                {type === 'streak' ? 'Hit Rate' : 'Confidence'}
              </p>
              <div className={`text-xl font-bold ${type === 'streak' ? getHitRateColor(hitRatePct) : 'text-blue-400'}`}>
                {progressLabel}
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-2 h-1.5 bg-gray-800 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                type === 'streak'
                  ? hitRatePct >= 90 ? 'bg-gradient-to-r from-green-500 to-emerald-400'
                  : hitRatePct >= 80 ? 'bg-gradient-to-r from-emerald-500 to-teal-400'
                  : 'bg-gradient-to-r from-yellow-500 to-amber-400'
                  : 'bg-gradient-to-r from-blue-500 to-indigo-400'
              }`}
              style={{ width: `${Math.min(progressValue, 100)}%` }}
            />
          </div>

          {/* Games window for streaks */}
          {type === 'streak' && games_window && (
            <p className="text-[10px] text-gray-500 mt-1.5">
              Hit in <span className="text-gray-300">{Math.round(hit_rate * games_window)}/{games_window}</span> recent games
            </p>
          )}
        </div>

        {/* Opponent info if available */}
        {opponent_abbr && (
          <div className="flex items-center justify-between text-xs mb-3 px-1">
            <div className="flex items-center gap-1.5 text-gray-400">
              <span>vs</span>
              <span className="font-semibold text-gray-300">{opponent_abbr}</span>
            </div>
            {opponent_strength !== undefined && (
              <div className="flex items-center gap-1">
                <ShieldCheckIcon className={`w-3.5 h-3.5 ${
                  opponent_strength >= 60 ? 'text-green-400' : opponent_strength >= 40 ? 'text-yellow-400' : 'text-red-400'
                }`} />
                <span className={`font-medium ${
                  opponent_strength >= 60 ? 'text-green-400' : opponent_strength >= 40 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {opponent_strength >= 60 ? 'Weak Def' : opponent_strength >= 40 ? 'Avg Def' : 'Strong Def'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Reasoning if available */}
        {reasoning && (
          <p className="text-xs text-gray-400 italic mb-3 line-clamp-2 px-1">
            "{reasoning}"
          </p>
        )}

        {/* Add to Parlay button */}
        <button
          onClick={isInParlay ? onRemove : onAdd}
          className={`
            w-full flex items-center justify-center gap-2 py-2.5 rounded-lg
            font-semibold text-sm transition-all duration-200
            ${isInParlay
              ? 'bg-green-600 hover:bg-green-500 text-white'
              : 'bg-purple-600 hover:bg-purple-500 text-white'
            }
          `}
        >
          {isInParlay ? (
            <>
              <CheckCircleIcon className="w-5 h-5" />
              Added to Parlay
            </>
          ) : (
            <>
              <PlusCircleIcon className="w-5 h-5" />
              Add to Parlay
            </>
          )}
        </button>
      </div>

      {/* Decorative corner gradient */}
      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-purple-500/10 to-transparent pointer-events-none" />
    </div>
  );
}
